import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SCHOOLS } from '../Schools/tokens';
import { parseCsvRecords } from '../Upload/parseCsvRow';
import { fetchUploadFile, type AdminUploadFile } from './uploadsAdminApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export const PREVIEW_ROW_LIMIT = 200;

type Props = {
  source?: (partition: string, schoolYear: string, version: number) => Promise<AdminUploadFile>;
};

const bodyText = {
  fontFamily: SCHOOLS.font,
  fontSize: 16,
  lineHeight: '24px',
  color: SCHOOLS.muted,
} as const;

const cellSx = {
  fontFamily: SCHOOLS.font,
  fontSize: 14,
  color: SCHOOLS.ink,
  borderBottom: `1px solid ${SCHOOLS.border}`,
  whiteSpace: 'nowrap',
} as const;

function downloadCsv(file: AdminUploadFile) {
  const url = URL.createObjectURL(new Blob([file.csv], { type: 'text/csv' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

type Loaded = {
  key: string;
  file: AdminUploadFile | null;
  failed: boolean;
};

export function AdminUploadFilePage({ source = fetchUploadFile }: Props = {}) {
  const { partition = '', year = '', version = '' } = useParams();
  const requestKey = `${partition}/${year}/${version}`;
  const [loaded, setLoaded] = useState<Loaded>({ key: requestKey, file: null, failed: false });

  useEffect(() => {
    let cancelled = false;
    source(partition, year, Number(version))
      .then((result) => {
        if (!cancelled) setLoaded({ key: requestKey, file: result, failed: false });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ key: requestKey, file: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [source, partition, year, version, requestKey]);

  const current = loaded.key === requestKey ? loaded : { file: null, failed: false };
  const { file, failed } = current;

  const records = useMemo(() => (file ? parseCsvRecords(file.csv) : []), [file]);
  const [header, ...rows] = records;

  return (
    <Box sx={{ display: 'flex', bgcolor: SCHOOLS.surface, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '24px 32px' }}>
        <Link
          component={RouterLink}
          to={`/admin/uploads/${encodeURIComponent(partition)}`}
          sx={{ fontFamily: SCHOOLS.font, fontSize: 14 }}
        >
          Back to this district&apos;s files
        </Link>
        {failed && (
          <Typography data-testid="admin-upload-file-error" sx={{ ...bodyText, mt: 2 }}>
            Could not load this file. Refresh to try again.
          </Typography>
        )}
        {!failed && file === null && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress data-testid="admin-upload-file-loading" size={24} />
          </Box>
        )}
        {!failed && file !== null && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Typography
                component="h1"
                sx={{ fontFamily: SCHOOLS.font, fontSize: 24, fontWeight: 700, color: SCHOOLS.ink }}
              >
                {file.name}
              </Typography>
              <Button
                data-testid="admin-upload-download"
                variant="outlined"
                size="small"
                onClick={() => downloadCsv(file)}
                sx={{ fontFamily: SCHOOLS.font, textTransform: 'none' }}
              >
                Download CSV
              </Button>
            </Box>
            <Typography sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}>
              {rows.length > PREVIEW_ROW_LIMIT
                ? `Showing the first ${PREVIEW_ROW_LIMIT} of ${rows.length} rows.`
                : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}.`}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{ bgcolor: SCHOOLS.cardBg, borderRadius: '4px', boxShadow: SCHOOLS.cardShadow }}
              >
                <TableHead>
                  <TableRow>
                    {(header ?? []).map((label, i) => (
                      <TableCell key={`${label}-${i}`} sx={{ ...cellSx, fontWeight: 700 }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(0, PREVIEW_ROW_LIMIT).map((row, rowIndex) => (
                    <TableRow key={rowIndex} data-testid="admin-upload-csv-row">
                      {row.map((value, i) => (
                        <TableCell key={i} sx={cellSx}>
                          {value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
